<template>
    <!-- Show dropdown only when backend available OR stop action; hide for start in frontend-only mode to avoid duplicate buttons -->
    <div class="dropdown" :class="{ 'is-active': isOpen }" v-if="(!store.isFrontendOnly) || (store.isFrontendOnly && actionType === 'stop')">
        <div class="dropdown-trigger">
            <button
                class="button"
                :class="dropdownTriggerClass"
                @click="toggleDropdown"
                :disabled="disabled"
                :aria-label="omitImmediate ? (actionType === 'start' ? 'Start timer at past time' : 'Stop timer at past time') : (actionType === 'start' ? 'Start or backdate timer' : 'Stop or backdate timer')"
            >
                <template v-if="!dropdownButtonOnly">
                    <span class="icon">
                        <FontAwesomeIcon :icon="icon" />
                    </span>
                    <span v-if="!omitImmediate">{{ buttonText }}</span>
                </template>
                <span class="icon is-small">
                    <FontAwesomeIcon :icon="faChevronDown" />
                </span>
            </button>
        </div>
        <div class="dropdown-menu" role="menu">
            <div class="dropdown-content">
                <!-- Full backdating options only when NOT frontend-only -->
                <template v-if="!store.isFrontendOnly">
                    <template v-if="customPresets && customPresets.length">
                        <a
                            v-for="(preset, index) in customPresets"
                            :key="index"
                            class="dropdown-item"
                            @click="handleCustomPreset(preset)"
                        >
                            {{ preset.label }}
                        </a>
                        <hr class="dropdown-divider">
                        <a class="dropdown-item" @click="openCustomPicker">
                            Custom time...
                        </a>
                    </template>
                    <template v-else>
                        <template v-if="showImmediateOption">
                            <a class="dropdown-item" @click="handlePresetClick(null)">
                                {{ actionType === 'start' ? 'Start Now' : 'Stop Now' }}
                            </a>
                            <hr class="dropdown-divider">
                        </template>
                        <a class="dropdown-item" @click="handlePresetClick(5)">
                            {{ actionType === 'start' ? 'Started 5 min ago' : 'Stopped 5 min ago' }}
                        </a>
                        <a class="dropdown-item" @click="handlePresetClick(10)">
                            {{ actionType === 'start' ? 'Started 10 min ago' : 'Stopped 10 min ago' }}
                        </a>
                        <a class="dropdown-item" @click="handlePresetClick(20)">
                            {{ actionType === 'start' ? 'Started 20 min ago' : 'Stopped 20 min ago' }}
                        </a>
                        <hr class="dropdown-divider">
                        <a class="dropdown-item" @click="openCustomPicker">
                            Custom time...
                        </a>
                    </template>
                </template>

                <!-- Add Completed Event option (when timer is not active and not frontend-only) -->
                <template v-if="actionType === 'start' && !store.isFrontendOnly && !isTimerActive">
                    <hr class="dropdown-divider">
                    <a class="dropdown-item" @click="openCompletedEventModal">
                        <span>Add Completed Event</span>
                    </a>
                </template>

                <!-- Discard option (always available in stop mode) -->
                <template v-if="actionType === 'stop'">
                    <hr v-if="!store.isFrontendOnly" class="dropdown-divider">
                    <a class="dropdown-item has-text-danger" @click="handleDiscard">
                        <span class="icon">
                            <FontAwesomeIcon :icon="faTrash" />
                        </span>
                        <span>Discard Session</span>
                    </a>
                </template>
            </div>
        </div>
    </div>

    <!-- Custom Time Picker Modal (Teleported to body for proper stacking) -->
    <Teleport to="body">
        <div v-if="showCustomPicker" class="modal sp-modal-card is-active">
            <div class="modal-background" @click="cancelCustomPicker"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ actionType === 'start' ? 'Start Timer At' : 'Stop Timer At' }}
                    </p>
                    <button class="delete" @click="cancelCustomPicker"></button>
                </header>
                <section class="modal-card-body">
                    <p v-if="timerScheduledTimes" class="mb-4 has-text-grey">
                        <strong>Timer scheduled:</strong> {{ timerScheduledTimes }}
                    </p>
                    <TimePicker 
                        v-model="customTime"
                        :label="actionType === 'start' ? 'Start time:' : 'End time:'"
                        :max-date="resolvedMaxDate"
                        :min-date="resolvedMinDate"
                        :error="customTimeError"
                    />
                    <p class="help">
                        Time must be within the allowed range 
                        ({{ formattedAllowedRange }})
                    </p>
                </section>
                <footer class="modal-card-foot">
                    <div class="buttons">
                        <button class="button is-primary" @click="handleCustomTime" :disabled="!customTime">
                            {{ actionType === 'start' ? 'Start' : 'Stop' }}
                        </button>
                        <button class="button" @click="cancelCustomPicker">Cancel</button>
                    </div>
                </footer>
            </div>
        </div>
    </Teleport>

    <!-- Completed Event Modal (Teleported to body for proper stacking) -->
    <Teleport to="body">
        <div v-if="showCompletedEventModal" class="modal sp-modal-card is-active">
            <div class="modal-background" @click="cancelCompletedEventModal"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">Add Completed Event</p>
                    <button class="delete" @click="cancelCompletedEventModal"></button>
                </header>
                <section class="modal-card-body">
                    <TimePicker 
                        v-model="completedEventStartTime"
                        label="Start time:"
                        :max-date="new Date()"
                        :min-date="sessionStartTime"
                        :error="completedEventError"
                    />
                    <TimePicker 
                        v-model="completedEventEndTime"
                        label="End time:"
                        :max-date="new Date()"
                        :min-date="sessionStartTime"
                        :error="completedEventError"
                    />
                    <p class="help">
                        Event must be within today's session 
                        ({{ formatTime(sessionStartTime) }} - {{ formatTime(new Date()) }})
                    </p>
                </section>
                <footer class="modal-card-foot">
                    <div class="buttons">
                        <button class="button is-primary" @click="handleCompletedEvent" 
                                :disabled="!completedEventStartTime || !completedEventEndTime">
                            Add Event
                        </button>
                        <button class="button" @click="cancelCompletedEventModal">Cancel</button>
                    </div>
                </footer>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlay, faStop, faChevronDown, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import TimePicker from './TimePicker.vue';
import { formatTime } from '../utils/time';
import { toastService } from '../utils/toastService.js';

const props = defineProps({
    timerId: {
        type: String,
        required: true
    },
    actionType: {
        type: String,
        required: true,
        validator: (value) => ['start', 'stop'].includes(value)
    },
    disabled: {
        type: Boolean,
        default: false
    },
    size: {
        type: String,
        default: 'normal',
        validator: (value) => ['small', 'normal'].includes(value)
    },
    // When true, do not include the immediate Start/Stop option in dropdown (used in split button mode)
    omitImmediate: {
        type: Boolean,
        default: false
    },
    // When true, render only the caret (dropdown trigger) without text/icon for immediate action
    dropdownButtonOnly: {
        type: Boolean,
        default: false
    },
    // Optional custom preset options (each: { label, startedAt?, endedAt? })
    customPresets: {
        type: Array,
        default: null
    },
    // Optional custom time picker preset value
    customTimePreset: {
        type: [String, Date],
        default: null
    },
    // Optional custom time picker bounds
    customMinDate: {
        type: [String, Date],
        default: null
    },
    customMaxDate: {
        type: [String, Date],
        default: null
    },
    // Optional reference label for timer's scheduled times
    timerScheduledTimes: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['action-completed', 'error']);

const store = useBlocksStore();

const isOpen = ref(false);
const showCustomPicker = ref(false);
const customTime = ref('');
const customTimeError = ref('');
const showCompletedEventModal = ref(false);
const completedEventStartTime = ref('');
const completedEventEndTime = ref('');
const completedEventError = ref('');

const buttonClass = computed(() => {
    const classes = [];
    if (props.actionType === 'start') {
        classes.push('is-primary');
    } else {
        classes.push('is-link');
    }
    if (props.size === 'small') {
        classes.push('is-small');
    }
    return classes;
});

// Class for dropdown trigger (can be smaller/neutral in split mode)
const dropdownTriggerClass = computed(() => {
    const base = [...buttonClass.value];
    // If showing only caret, make it outlined to visually separate from main action button
    return base;
});

const buttonText = computed(() => props.actionType === 'start' ? 'Start Timer' : 'Stop Timer');

const icon = computed(() => {
    return props.actionType === 'start' ? faPlay : faStop;
});

const sessionStartTime = computed(() => {
    // Get session start time (midnight of current day)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
});

const resolvedMinDate = computed(() => {
    if (props.customMinDate) return new Date(props.customMinDate);
    return sessionStartTime.value;
});

const resolvedMaxDate = computed(() => {
    if (props.customMaxDate) return new Date(props.customMaxDate);
    return new Date();
});

const formattedAllowedRange = computed(() => {
    const minDate = resolvedMinDate.value;
    const maxDate = resolvedMaxDate.value;
    const sameDay = minDate.toDateString() === maxDate.toDateString();
    
    if (sameDay) {
        return `${formatTime(minDate)} - ${formatTime(maxDate)}`;
    }
    
    const formatWithDate = (d) => d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
    return `${formatWithDate(minDate)} - ${formatWithDate(maxDate)}`;
});

const isTimerActive = computed(() => {
    const timer = store.blocks.find(block => block.id === props.timerId);
    return timer ? timer.isActive : false;
});

const toggleDropdown = () => { isOpen.value = !isOpen.value; };

const closeDropdown = () => { isOpen.value = false; };

const showImmediateOption = computed(() => !props.omitImmediate);

const handlePresetClick = async (minutesAgo) => {
    closeDropdown();

    try {
        const options = {};
        
    if (minutesAgo) {
            const targetTime = new Date();
            targetTime.setMinutes(targetTime.getMinutes() - minutesAgo);
            
            if (props.actionType === 'start') {
                options.startedAt = targetTime.toISOString();
            } else {
                options.endedAt = targetTime.toISOString();
            }
        }

        let result;
    if (props.actionType === 'start') {
            result = await store.startTimer(props.timerId, options);
        } else {
            result = await store.stopTimer(props.timerId, options);
        }

        // Handle errors from store methods
        if (result && !result.success) {
            handleApiError(result);
            return;
        }

        emit('action-completed', { 
            action: props.actionType, 
            minutesAgo,
            options 
        });

    } catch (error) {
        console.error('Error in timer action:', error);
        toastService.error('An unexpected error occurred');
        emit('error', error);
    }
};

const handleCustomPreset = async (preset) => {
    closeDropdown();

    try {
        const options = {};
        const presetValue = props.actionType === 'start'
            ? (preset.startedAt || preset.endedAt)
            : (preset.endedAt || preset.startedAt);

        if (presetValue) {
            const targetTime = new Date(presetValue);
            if (props.actionType === 'start') {
                options.startedAt = targetTime.toISOString();
            } else {
                options.endedAt = targetTime.toISOString();
            }
        }

        let result;
        if (props.actionType === 'start') {
            result = await store.startTimer(props.timerId, options);
        } else {
            result = await store.stopTimer(props.timerId, options);
        }

        if (result && !result.success) {
            handleApiError(result);
            return;
        }

        emit('action-completed', {
            action: props.actionType,
            preset,
            options
        });
    } catch (error) {
        console.error('Error in timer action:', error);
        toastService.error('An unexpected error occurred');
        emit('error', error);
    }
};

// Discard currently running session (do not add duration)
const handleDiscard = async () => {
    closeDropdown();

    if (!confirm('Discard this active session? This will remove the currently running session without recording time.')) {
        return;
    }

    try {
        if (store.isFrontendOnly) {
            const blockIndex = store.blocks.findIndex(block => block.id === props.timerId);
            if (blockIndex !== -1) {
                store.blocks[blockIndex].isActive = false;
                store.blocks[blockIndex].activeEventStartDate = null;
                store.blocks[blockIndex].activeEventId = null;
            }
            store.saveBlocksToLocalStorage();
            toastService.success('Session discarded');
            emit('action-completed', { action: 'discard', options: {} });
            return;
        }

    // Backend mode: discard active session only
    const result = await store.discardActiveEvent(props.timerId);
        if (result && !result.success) {
            handleApiError(result);
            return;
        }
        
        toastService.success('Session discarded');
        emit('action-completed', { action: 'discard', options: {} });
    } catch (error) {
        console.error('Error discarding timer:', error);
        toastService.error('An error occurred while discarding');
        emit('error', error);
    }
};

const handleCustomTime = async () => {
    if (!customTime.value) return;

    customTimeError.value = '';

    try {
        const targetTime = new Date(customTime.value);
        const now = new Date();
        const minAllowed = resolvedMinDate.value;
        const maxAllowed = resolvedMaxDate.value;

        // Validate time is in the past
        if (targetTime > maxAllowed) {
            customTimeError.value = 'Time cannot be in the future';
            return;
        }

        // Validate time is within session boundaries  
        if (targetTime < minAllowed) {
            customTimeError.value = 'Time is before the allowed range';
            return;
        }

        const options = {};
        if (props.actionType === 'start') {
            options.startedAt = targetTime.toISOString();
        } else {
            options.endedAt = targetTime.toISOString();
        }

        let result;
        if (props.actionType === 'start') {
            result = await store.startTimer(props.timerId, options);
        } else {
            result = await store.stopTimer(props.timerId, options);
        }

        // Handle errors from store methods
        if (result && !result.success) {
            handleApiError(result);
            return;
        }

        showCustomPicker.value = false;
        customTime.value = '';

        const action = props.actionType === 'start' ? 'started' : 'stopped';

        emit('action-completed', { 
            action: props.actionType, 
            customTime: targetTime,
            options 
        });

    } catch (error) {
        console.error('Error in custom timer action:', error);
        customTimeError.value = 'An error occurred';
        emit('error', error);
    }
};

// Open custom picker and prefill default start time for start action
const openCustomPicker = () => {
    if (props.customTimePreset) {
        const presetDate = new Date(props.customTimePreset);
        customTime.value = isNaN(presetDate.getTime()) ? '' : presetDate.toISOString();
    } else if (props.actionType === 'start') {
        const now = new Date();
        // Format to YYYY-MM-DDTHH:MM in local timezone
        const pad = (n) => String(n).padStart(2, '0');
        const local = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
        // Store ISO string so existing logic works (will still show shifted if using toISOString in TimePicker)
        customTime.value = new Date(local).toISOString();
    } else {
        // Prefill stop time: max(time when elapsed = original duration, now)
        const timer = store.blocks.find(block => block.id === props.timerId);
        const now = new Date();
        if (timer && timer.activeEventStartDate && timer.endDate && timer.startDate) {
            const originalDurationMs = timer.endDate.getTime() - timer.startDate.getTime();
            const previousDurationMs = (timer.duration || 0) * 1000;
            const durationCompleteTime = new Date(timer.activeEventStartDate.getTime() + originalDurationMs - previousDurationMs);
            const prefillDate = new Date(Math.min(durationCompleteTime.getTime(), now.getTime()));
            const pad = (n) => String(n).padStart(2, '0');
            const local = `${prefillDate.getFullYear()}-${pad(prefillDate.getMonth()+1)}-${pad(prefillDate.getDate())}T${pad(prefillDate.getHours())}:${pad(prefillDate.getMinutes())}`;
            customTime.value = new Date(local).toISOString();
        } else {
            customTime.value = '';
        }
    }
    showCustomPicker.value = true;
    isOpen.value = false;
};

const openCompletedEventModal = () => {
    // Default to current time minus 30 minutes for end, and minus 60 minutes for start
    const now = new Date();
    const endTime = new Date(now.getTime() - 30 * 60000); // 30 minutes ago
    const startTime = new Date(now.getTime() - 60 * 60000); // 60 minutes ago

    const pad = (n) => String(n).padStart(2, '0');
    const formatLocal = (date) => `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    
    completedEventStartTime.value = new Date(formatLocal(startTime)).toISOString();
    completedEventEndTime.value = new Date(formatLocal(endTime)).toISOString();
    
    showCompletedEventModal.value = true;
    isOpen.value = false;
};

const handleApiError = (result) => {
    const messages = {
        session_boundary: 'Time must be within today\'s session',
        invalid_time_range: 'Invalid time range',
        overlap_conflict: 'This would create overlapping sessions',
        running_event: 'Cannot edit a running session',
        frontend_only_unsupported: 'Offline mode: cannot add completed sessions'
    };
    
    const message = messages[result.error] || result.message || 'An error occurred';
    toastService.error(message);
    emit('error', result);
};

const cancelCustomPicker = () => {
    showCustomPicker.value = false;
    customTime.value = '';
    customTimeError.value = '';
};

const cancelCompletedEventModal = () => {
    showCompletedEventModal.value = false;
    completedEventStartTime.value = '';
    completedEventEndTime.value = '';
    completedEventError.value = '';
};

const handleCompletedEvent = async () => {
    if (!completedEventStartTime.value || !completedEventEndTime.value) return;

    completedEventError.value = '';

    try {
        const startTime = new Date(completedEventStartTime.value);
        const endTime = new Date(completedEventEndTime.value);
        const now = new Date();
        const sessionStart = sessionStartTime.value;

        // Validate times are in the past
        if (startTime > now || endTime > now) {
            completedEventError.value = 'Times cannot be in the future';
            return;
        }

        // Validate times are within session boundaries  
        if (startTime < sessionStart || endTime < sessionStart) {
            completedEventError.value = 'Times must be within today\'s session';
            return;
        }

        // Validate start is before end
        if (startTime >= endTime) {
            completedEventError.value = 'Start time must be before end time';
            return;
        }

        // Use store method to create completed event
        const result = await store.addCompletedEvent(props.timerId, {
            startedAt: startTime.toISOString(),
            endedAt: endTime.toISOString()
        });

        // Handle errors from store method
        if (result && !result.success) {
            handleApiError(result);
            return;
        }

        showCompletedEventModal.value = false;
        completedEventStartTime.value = '';
        completedEventEndTime.value = '';

        emit('action-completed', { 
            action: 'completed_event_added', 
            startTime,
            endTime
        });

    } catch (error) {
        console.error('Error creating completed event:', error);
        completedEventError.value = 'An error occurred';
        emit('error', error);
    }
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
    if (!event.target.closest('.dropdown')) {
        closeDropdown();
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Ensure dropdown overlays modal footer/content */
.dropdown-menu {
    z-index: 50; /* Above modal content */
}

/* When used inside has-addons, prevent rounded gap */
.dropdown-trigger > .button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

/* Root dropdown for positioning */
.dropdown.is-active {
    position: relative;
}

/* Match modal body overflow behavior (consistent with other modals) */
.modal-card-body {
    overflow: visible;
}
</style>